import * as Sentry from '@sentry/browser';
import fetch from 'isomorphic-fetch';
import { PureComponent } from 'react';

import LoadingPage from 'my-components/LoadingPage';

import ClientError from './ClientError';

const RELOADED_CHUNKS_KEY = 'chunk-load-reloads';

export default class MainErrorBoundary extends PureComponent {
  state = { error: null, eventId: null, hasRetried: false };
  retriedRequests = {};

  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, errorInfo) {
    if (error.name !== 'ChunkLoadError') {
      this.setState({ eventId: Sentry.captureException(error, { extra: errorInfo }) });
      return;
    }
    // `fetch(undefined)` resolves against the current page and comes back 200, which would look
    // like a healthy chunk and send us into a retry that can only fail again.
    if (!error.request) {
      this.setState({
        eventId: Sentry.captureException(error, { tags: { 'chunk-load-error': 'no-request' } }),
        hasRetried: true,
      });
      return;
    }
    this.probeChunk(error);
  }
  probeChunk(error) {
    let resp = null;
    fetch(error.request)
      .then(response => {
        resp = response;
        return response.text();
      })
      .then(text => {
        if (resp.ok && !this.retriedRequests[error.request]) {
          this.retriedRequests[error.request] = true;
          // Clearing the error remounts the tree so loadable re-requests the chunk. Resetting
          // `hasRetried` keeps a later, unrelated chunk failure eligible for its own recovery.
          this.setState({ error: null, eventId: null, hasRetried: false });
          return;
        }
        const eventId = Sentry.captureException(error, {
          tags: { 'chunk-load-error': resp.status },
          extra: {
            response: {
              status: resp.status,
              statusText: resp.statusText,
              body: text?.slice(0, 1000),
            },
          },
        });
        this.setState({ eventId, hasRetried: true });
        if (resp.status === 404) {
          this.reloadForStaleChunk(error.request);
        }
      })
      .catch(e => {
        const eventId = Sentry.captureException(error, {
          tags: { 'chunk-load-error': 'error' },
          extra: { error: e },
        });
        this.setState({ eventId, hasRetried: true });
      });
  }
  // A 404 means this document was served by an older deploy whose chunks are gone, which a
  // reload fixes. Record the chunk first so a document still being served from cache can't put
  // us in a reload loop, and skip the reload entirely if we can't record it.
  reloadForStaleChunk(request) {
    try {
      const reloaded = JSON.parse(window.sessionStorage.getItem(RELOADED_CHUNKS_KEY)) || [];
      if (reloaded.includes(request)) return;
      window.sessionStorage.setItem(RELOADED_CHUNKS_KEY, JSON.stringify([...reloaded, request]));
    } catch (ignoreStorageError) {
      return;
    }
    window.location.reload();
  }
  render() {
    const { error, eventId, hasRetried } = this.state;

    if (error) {
      if (error.name === 'ChunkLoadError' && !hasRetried) {
        return <LoadingPage />;
      }
      return (
        <ClientError
          error={error}
          eventId={eventId}
          onResetError={() => this.setState({ error: null, eventId: null, hasRetried: false })}
        />
      );
    }
    return this.props.children;
  }
}
